import { getPlatform, isNativePlatform } from '../utils/platform';
import { errorReporter } from './errorReporter';

/**
 * Server-driven minimum-version gate (DP-2204).
 *
 * Fetches cdn.dentalpost.net/mobile/upgrade/min-version-{platform}.json
 * ({ "min": "10.0.0", "upgrade": "<store-url>" }) at launch and on app
 * resume, compares numerically against the installed version, and exposes
 * a subscribable gated/not-gated state consumed by <ForceUpgradeGate />.
 *
 * Fail-open by design: if the fetch fails and there is no cached config,
 * the user is never locked out on a network blip.
 */

export type MinVersionConfig = {
  min?: string;
  upgrade?: string;
  // Optional soft-nudge tier ("recommended") may also exist in the JSON;
  // unknown fields are ignored, so adding it later needs no app change.
  // If a future min requires a newer OS than this device can run, the
  // file's minOsMajor keeps us from gating users who cannot upgrade.
  minOsMajor?: number;
};

export type VersionGateState = {
  gated: boolean;
  upgradeUrl: string;
};

const CACHE_KEY = 'dp-min-version-config';
const FETCH_TIMEOUT_MS = 5000;

const DEFAULT_STORE_URLS: Record<string, string> = {
  ios: 'https://apps.apple.com/app/id504971545',
  android:
    'https://play.google.com/store/apps/details?id=net.dentalpost.mobile',
};

const UNGATED: VersionGateState = { gated: false, upgradeUrl: '' };

let state: VersionGateState = UNGATED;
let initialized = false;
const subscribers = new Set<() => void>();

export function getVersionGateState(): VersionGateState {
  return state;
}

export function subscribeToVersionGate(callback: () => void): () => void {
  subscribers.add(callback);
  return () => {
    subscribers.delete(callback);
  };
}

function setState(next: VersionGateState): void {
  if (next.gated === state.gated && next.upgradeUrl === state.upgradeUrl) {
    return;
  }
  state = next;
  subscribers.forEach((cb) => cb());
}

/**
 * Numeric segment-by-segment compare — "9.9" < "9.10" must hold, which
 * string comparison gets wrong. Returns true when a < b.
 */
export function isVersionBelow(a: string, b: string): boolean {
  const pa = a.split('.').map((s) => parseInt(s, 10));
  const pb = b.split('.').map((s) => parseInt(s, 10));
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
    const x = Number.isNaN(pa[i]) || pa[i] === undefined ? 0 : pa[i];
    const y = Number.isNaN(pb[i]) || pb[i] === undefined ? 0 : pb[i];
    if (x !== y) return x < y;
  }
  return false;
}

async function getCurrentVersion(): Promise<string> {
  if (isNativePlatform()) {
    const { App } = await import('@capacitor/app');
    const info = await App.getInfo();
    return info.version;
  }
  return process.env.GATSBY_APP_VERSION ?? '0.0.0';
}

function deviceOsMajor(): number | null {
  if (typeof navigator === 'undefined') return null;
  const ua = navigator.userAgent;
  const m = ua.match(/iPhone OS (\d+)/) || ua.match(/Android (\d+)/);
  return m ? parseInt(m[1], 10) : null;
}

async function fetchMinVersionConfig(
  platform: string
): Promise<MinVersionConfig | null> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
  try {
    const res = await fetch(
      `https://cdn.dentalpost.net/mobile/upgrade/min-version-${platform}.json`,
      { cache: 'no-store', signal: controller.signal }
    );
    if (!res.ok) return null;
    const json = (await res.json()) as MinVersionConfig;
    return typeof json?.min === 'string' ? json : null;
  } catch (err) {
    // A CORS-blocked response lands here as a bare TypeError while the CDN
    // logs a 200 — without this log the gate is indistinguishable from
    // "no file uploaded yet" (learned the hard way, see PR #72).
    console.warn('[versionGate] min-version fetch failed:', err);
    return null;
  } finally {
    clearTimeout(timer);
  }
}

async function readCachedConfig(): Promise<MinVersionConfig | null> {
  try {
    const { Preferences } = await import('@capacitor/preferences');
    const { value } = await Preferences.get({ key: CACHE_KEY });
    if (!value) return null;
    const json = JSON.parse(value) as MinVersionConfig;
    return typeof json?.min === 'string' ? json : null;
  } catch {
    return null;
  }
}

async function writeCachedConfig(config: MinVersionConfig): Promise<void> {
  try {
    const { Preferences } = await import('@capacitor/preferences');
    await Preferences.set({ key: CACHE_KEY, value: JSON.stringify(config) });
  } catch {
    // Cache write failure only costs offline coverage — never surface it.
  }
}

export async function checkVersionGate(): Promise<void> {
  if (!isNativePlatform()) return;
  try {
    await runVersionGateCheck();
  } catch (err) {
    // Fail-open is the design, but never silently — an unlogged failure
    // here means the gate can be permanently inert with no visible signal.
    console.error('[versionGate] check failed (failing open):', err);
    errorReporter.report(err, 'warning').catch(() => undefined);
  }
}

async function runVersionGateCheck(): Promise<void> {
  const platform = getPlatform();
  let config = await fetchMinVersionConfig(platform);
  if (config) {
    await writeCachedConfig(config);
  } else {
    config = await readCachedConfig();
  }
  // Fail-open: no fetched config and no cache → user stays in.
  if (!config?.min) {
    setState(UNGATED);
    return;
  }

  // Soft-block: if the required update needs a newer OS than this device
  // runs, gating would tell the user to install something they can't.
  const osMajor = deviceOsMajor();
  if (config.minOsMajor && osMajor !== null && osMajor < config.minOsMajor) {
    setState(UNGATED);
    return;
  }

  const current = await getCurrentVersion();
  if (isVersionBelow(current, config.min)) {
    setState({
      gated: true,
      upgradeUrl: config.upgrade || DEFAULT_STORE_URLS[platform] || '',
    });
  } else {
    setState(UNGATED);
  }
}

export async function initializeVersionGate(): Promise<void> {
  if (!isNativePlatform() || initialized) return;
  initialized = true;

  const { App } = await import('@capacitor/app');
  App.addListener('appStateChange', ({ isActive }) => {
    // Re-check on resume so a long-lived session still gets gated after
    // the server-side min is raised.
    if (isActive) void checkVersionGate();
  });

  void checkVersionGate();
}

export function openUpgradeUrl(): void {
  const url = state.upgradeUrl || DEFAULT_STORE_URLS[getPlatform()] || '';
  if (!url) return;
  // Store URLs are cross-origin by definition, so openInCapacitorBrowser's
  // same-origin guard would reject them — open via Browser directly.
  import('@capacitor/browser')
    .then(({ Browser }) => Browser.open({ url }))
    .catch((err) => {
      console.error('[versionGate] failed to open store URL:', err);
    });
}
